import { useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import ReceiptIcon from '@mui/icons-material/ReceiptLong'
import MailIcon from '@mui/icons-material/MailOutline'
import BlockIcon from '@mui/icons-material/Block'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined'
import UndoIcon from '@mui/icons-material/UndoOutlined'
import EditIcon from '@mui/icons-material/EditOutlined'
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import {
  format,
  money,
  dateHeureLisible,
  PAYMENT_METHOD_LABELS,
  eanDeDocument,
  isMutableParAdmin,
  horairesParDefaut,
  libelleStatut,
  referenceAffichable,
  REFUS_ANNULER_COMMANDE_FACTUREE,
  resteDu,
  soldeDocument,
  type CurrencyCode,
  type PaymentMethod,
  type TransactionLineInput,
} from '@iobiz/shared'
import { useVente } from '../data/hooks.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { api } from '../api/client.js'
import { runCycle } from '../data/sync/engine.js'
import { factureIssueDe } from '../data/repositories/sales.local.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { Barcode } from '../ui/Barcode.js'
import { FactureImprimable } from '../features/sales/FactureImprimable.js'
import { HistoriqueRevisions } from '../features/sales/HistoriqueRevisions.js'
import { ModifierVenteDialog } from '../features/sales/ModifierVenteDialog.js'
import { MontantDialog } from '../features/sales/MontantDialog.js'
import { RattacherClientDialog } from '../features/sales/RattacherClientDialog.js'
import { PriseRendezVousDialog } from '../features/agenda/PriseRendezVousDialog.js'
import { TicketCaisse } from '../features/sales/TicketCaisse.js'
import {
  mesurerPageTicket,
  PortailImpression,
  ReglesImpression,
  type FormatImpression,
} from '../features/sales/impression.js'

/**
 * Détail d'une vente (W-17).
 *
 * ⭐ La fiche est en LECTURE. Un document validé porte un numéro légal et une
 * numérotation continue : le modifier après coup rendrait la comptabilité
 * fausse sans laisser de trace. On l'annule — ce qui rend le stock et garde
 * les deux faits — puis on ré-encaisse.
 */

/**
 * Couleur du statut. Le TEXTE, lui, vient du paquet partagé : il dépend du type
 * de document (« À régler » sur une facture, « Émis » sur un devis).
 */
const COULEURS = {
  draft: 'default',
  validated: 'info',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'error',
} as const

const TYPES: Record<string, string> = {
  quote: 'Devis',
  invoice: 'Vente',
  deposit_invoice: 'Acompte',
  credit_note: 'Avoir',
  order: 'Commande',
}

export function SaleDetailPage(): JSX.Element {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const utilisateur = useSessionStore((s) => s.user)
  const vente = useVente(store?.storeId, id)

  const [confirmation, setConfirmation] = useState(false)
  const [envoiOuvert, setEnvoiOuvert] = useState(false)
  const [adresse, setAdresse] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [impression, setImpression] = useState<FormatImpression>('facture')
  /**
   * Format de page du ticket, `72mm <hauteur>mm`, mesuré juste avant d'imprimer.
   *
   * ⚠️ `size: 80mm auto` NE SUFFIT PAS sur une thermique. « auto » laisse la
   * hauteur au pilote, et le pilote d'un rouleau propose des pages FIXES —
   * 210 mm, 297 mm, 3276 mm. La sienne par défaut valant 297 mm, chaque ticket
   * déroulait la hauteur d'une A4 pour trois lignes d'articles. On lui donne
   * donc la hauteur réelle du bloc, la seule qu'il ne peut pas arrondir.
   */
  const [pageTicket, setPageTicket] = useState<string | null>(null)
  const [avoirOuvert, setAvoirOuvert] = useState(false)
  const [modificationOuverte, setModificationOuverte] = useState(false)
  const [acompteOuvert, setAcompteOuvert] = useState(false)
  const [clientOuvert, setClientOuvert] = useState(false)
  const [rdvOuvert, setRdvOuvert] = useState(false)

  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const administrateur = utilisateur?.isPlatformAdmin || store?.role === 'store_admin'

  if (vente === undefined) {
    return <Typography sx={{ color: 'text.secondary' }}>Chargement…</Typography>
  }
  if (!vente) {
    return (
      <Alert severity="error" action={<Button onClick={() => navigate(-1)}>Retour</Button>}>
        Vente introuvable sur cet appareil.
      </Alert>
    )
  }

  const etiquette = {
    texte: libelleStatut(vente.docType, vente.status),
    couleur: COULEURS[vente.status],
  }
  const reference = referenceAffichable(vente)
  const ean = eanDeDocument(vente.docType, vente.number)
  /**
   * ⚠️ Un DEVIS emprunte cette fiche à la vente, et en héritait des mentions
   * qui n'ont aucun sens pour lui : « encaissée par », « Réglé 0 », et surtout
   * un « Reste dû » en orange sur une proposition que le client n'a même pas
   * acceptée. L'écran réclamait de l'argent que personne ne devait.
   */
  const estDevis = vente.docType === 'quote'
  /**
   * ⭐ Une COMMANDE se transforme en facture, comme un devis se transforme en
   * vente — même geste, même écran de caisse pré-rempli. La différence tient
   * au stock : une commande l'a déjà réservé à sa validation (voir
   * `affectsStock`, côté serveur), la facture qui en découle ne le sort donc
   * pas une seconde fois.
   */
  const estCommande = vente.docType === 'order'
  /** Ni le devis ni la commande ne s'encaissent directement : ce sont des engagements. */
  const estEngagement = estDevis || estCommande

  /**
   * Change de document PUIS imprime.
   *
   * ⚠️ `window.print()` fige la page telle qu'elle est à l'instant de l'appel :
   * appelé juste après `setImpression`, il sortirait le format PRÉCÉDENT, React
   * n'ayant pas encore peint. D'où `flushSync`, qui force le rendu avant de
   * rendre la main.
   *
   * ⚠️ La version d'avant passait par un état « à imprimer » et un effet. Elle
   * ne marchait PAS : l'effet remettait le drapeau à faux, ce changement
   * déclenchait son propre nettoyage, et le nettoyage annulait le minuteur
   * d'impression avant qu'il ne parte. Aucune fenêtre d'impression ne
   * s'ouvrait, ni pour le ticket ni pour la facture — et rien à l'écran ne le
   * disait. Le rendu synchrone supprime le minuteur, donc le défaut.
   */
  function imprimer(quoi: FormatImpression): void {
    setMenu(null)
    flushSync(() => {
      setImpression(quoi)
      setPageTicket(null)
    })
    // ⭐ La hauteur se MESURE, elle ne se devine pas : voir `pageTicket`.
    if (quoi === 'ticket') flushSync(() => setPageTicket(mesurerPageTicket()))
    window.print()
  }

  async function annuler(): Promise<void> {
    if (!store || !vente) return
    try {
      /**
       * ⚠️ La réplique d'abord : une facture faite en caisse attend peut-être
       * encore dans la file de synchronisation. Le serveur, qui ne la voit
       * pas, accepterait l'annulation — et la réservation serait rendue alors
       * que la marchandise est vendue. Le refus est le sien, mot pour mot.
       */
      if (vente.docType === 'order' && (await factureIssueDe(store.storeId, vente.id))) {
        setMessage(REFUS_ANNULER_COMMANDE_FACTUREE)
        return
      }
      await api.post(`/stores/${store.storeId}/transactions/${vente.id}/cancel`, undefined, {
        sansAppareil: true,
      })
      await runCycle(store.storeId)
      setMessage(
        vente.docType === 'quote'
          ? 'Devis marqué refusé.'
          : vente.docType === 'order'
            ? 'Commande annulée. Le stock réservé a été rendu.'
            : 'Vente annulée. Le stock a été rendu.',
      )
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Annulation impossible.')
    } finally {
      setConfirmation(false)
    }
  }

  async function emettreAvoir(montant: number): Promise<void> {
    if (!store || !vente) return
    try {
      const avoir = await api.post<{ id: string; number: string | null }>(
        `/stores/${store.storeId}/transactions/${vente.id}/credit-note`,
        { amount: montant },
        { sansAppareil: true },
      )
      await runCycle(store.storeId)
      setAvoirOuvert(false)
      // On ouvre l'avoir : c'est le document qu'on veut imprimer et remettre.
      navigate(`/s/${slug}/ventes/${avoir.id}`)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Émission impossible.')
    }
  }

  async function enregistrerAcompte(montant: number, moyen: PaymentMethod): Promise<void> {
    if (!store || !vente) return
    try {
      await api.post(
        `/stores/${store.storeId}/transactions/${vente.id}/payments`,
        { method: moyen, amount: montant },
        { sansAppareil: true },
      )
      await runCycle(store.storeId)
      setAcompteOuvert(false)
      setMessage(`Acompte de ${prix(montant)} enregistré.`)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  /**
   * Enregistre une correction, motif compris.
   *
   * ⚠️ `runCycle` ensuite : la fiche lit la RÉPLIQUE, qui afficherait encore
   * les anciennes lignes jusqu'au prochain cycle — le commerçant croirait sa
   * correction perdue et la referait.
   */
  async function modifier(lignes: TransactionLineInput[], motif: string): Promise<void> {
    if (!store || !vente) return
    try {
      await api.put(
        `/stores/${store.storeId}/transactions/${vente.id}`,
        {
          docType: vente.docType,
          ...(vente.customerId ? { customerId: vente.customerId } : {}),
          issueDate: vente.issueDate,
          currencyCode: vente.currencyCode,
          globalDiscountRate: 0,
          notes: vente.notes ?? undefined,
          terms: vente.terms ?? undefined,
          lines: lignes,
          ...(motif ? { motif } : {}),
        },
        { sansAppareil: true },
      )
      await runCycle(store.storeId)
      setModificationOuverte(false)
      setMessage('Document modifié. La version précédente est archivée.')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Modification impossible.')
    }
  }

  async function envoyer(): Promise<void> {
    if (!store || !vente) return
    try {
      const r = await api.post<{ destinataire: string }>(
        `/stores/${store.storeId}/transactions/${vente.id}/send`,
        adresse ? { email: adresse } : {},
        { sansAppareil: true },
      )
      setMessage(`Document envoyé à ${r.destinataire}.`)
      setEnvoiOuvert(false)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Envoi impossible.')
    }
  }

  return (
    <Box>
      {/* ⚠️ Le format de PAGE suit le document : un ticket de caisse sur du
          A4 sort sur une feuille aux trois quarts vide, et une facture sur un
          rouleau de 80 mm sort tronquée. */}
      <ReglesImpression format={impression} pageTicket={pageTicket} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h1">{reference}</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
            <Chip label={TYPES[vente.docType] ?? vente.docType} size="small" variant="outlined" />
            <Chip label={etiquette.texte} color={etiquette.couleur} size="small" />
            {/* La pastille reprend son sens pour les devis depuis qu'ils sont
                numérotés : le numéro définitif arrive au retour du réseau,
                comme pour une vente. */}
            {!vente.number && (
              <Chip label="référence provisoire" size="small" variant="outlined" color="warning" />
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {dateHeureLisible(vente.createdAt)}
              {vente.cashierName
                ? ` · ${
                    estDevis ? 'établi par' : estCommande ? 'établie par' : 'encaissée par'
                  } ${vente.cashierName}`
                : ''}
            </Typography>
          </Box>
        </Box>

        {/* ⭐ Les actions passent sous un MENU. Alignées en façade, elles
            mettaient « Annuler la vente » — irréversible — à deux centimètres
            d'« Imprimer », et la barre s'allongeait à chaque ajout. Seule
            l'impression du ticket reste en évidence : c'est le geste du
            comptoir, répété toute la journée. */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* ⚠️ Ni un DEVIS ni une COMMANDE ne s'impriment en ticket de
              caisse : ce sont des engagements, pas des justificatifs d'achat,
              et le rouleau de 80 mm n'est pas ce qu'on remet à un client qui
              réfléchit ou qui attend une livraison. */}
          {estEngagement ? (
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => imprimer('facture')}
            >
              {estDevis ? 'Imprimer le devis' : 'Imprimer le bon de commande'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<ReceiptIcon />}
              onClick={() => imprimer('ticket')}
            >
              Ticket de caisse
            </Button>
          )}
          <IconButton
            aria-label="Actions sur la vente"
            onClick={(e) => setMenu(e.currentTarget)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
            {!estEngagement && (
              <MenuItem onClick={() => imprimer('ticket')}>
                <ListItemIcon>
                  <ReceiptIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Imprimer le ticket de caisse"
                  secondary="Rouleau 80 mm, entête et code-barres"
                />
              </MenuItem>
            )}
            <MenuItem onClick={() => imprimer('facture')}>
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  estDevis
                    ? 'Imprimer le devis'
                    : estCommande
                      ? 'Imprimer le bon de commande'
                      : 'Imprimer la facture'
                }
                secondary="A4, détail de la TVA"
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenu(null)
                setEnvoiOuvert(true)
              }}
            >
              <ListItemIcon>
                <MailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Envoyer par courriel" />
            </MenuItem>
            {/* ⚠️ Plus « status === draft » : un devis ou une commande
                numérotés sont « validés », et l'action disparaissait au
                premier aller-retour réseau — juste après être devenus
                présentables au client.
                ⭐ MÊME chemin pour les deux : la caisse reprend les lignes, le
                commerçant ajuste puis encaisse. Le document d'origine, lui, ne
                bouge pas — c'est un engagement, pas un brouillon qu'on efface
                en le transformant. La facture qui en sort porte son
                identifiant (`origineId`), et — pour une commande — le serveur
                sait alors que le stock a déjà été réservé et ne le ressort
                pas une seconde fois. */}
            {estEngagement && vente.status !== 'cancelled' && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  navigate(`/s/${slug}/caisse`, {
                    state: {
                      lignes: vente.lines.map((l) => ({
                        itemId: l.itemId,
                        label: l.label,
                        unitPrice: l.unitPrice,
                        taxRate: l.taxRate,
                        quantity: l.quantity,
                      })),
                      customerId: vente.customerId,
                      // L'IDENTIFIANT, pas la référence : c'est lui qui
                      // rattachera la vente au document d'origine.
                      origineId: vente.id,
                    },
                  })
                }}
              >
                <ListItemIcon>
                  <PointOfSaleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={estDevis ? 'Accepté — transformer en vente' : 'Transformer en facture'}
                  secondary="Ouvre la caisse pré-remplie"
                />
              </MenuItem>
            )}
            {/* ⭐ Le REFUS est l'autre issue d'un devis, et la seule qui n'ait
                aucune trace ailleurs : un devis accepté devient une vente, un
                devis refusé ne devient rien. Sans cette action, les devis sans
                suite s'accumulaient en « Émis » pour toujours. */}
            {estDevis && vente.status !== 'cancelled' && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setConfirmation(true)
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Refusé" secondary="Le client n’a pas donné suite" />
              </MenuItem>
            )}
            {/* ⭐ Le client OUBLIÉ. L'entrée n'apparaît que sur un document
                qui n'en porte pas : proposer « rattacher » sur une vente déjà
                attribuée laisserait croire qu'on peut la réattribuer, ce que
                le serveur refuse — et à raison. */}
            {!vente.customerId && vente.status !== 'cancelled' && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setClientOuvert(true)
                }}
              >
                <ListItemIcon>
                  <PersonAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Rattacher un client"
                  secondary="Vente enregistrée sans client"
                />
              </MenuItem>
            )}

            {/* ⭐ Le rendez-vous OUBLIÉ : la prestation a été vendue, le créneau
                jamais posé. Aucun document n'est créé — la vente existe déjà. */}
            {vente.customerId && vente.status !== 'cancelled' && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setRdvOuvert(true)
                }}
              >
                <ListItemIcon>
                  <EventAvailableIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Ajouter un rendez-vous"
                  secondary="Bloquer un créneau pour cette vente"
                />
              </MenuItem>
            )}

            {/* ⚠️ Ni le devis ni la commande n'ont d'acompte : rien n'est dû
                tant qu'ils n'ont pas été transformés en facture — c'est ELLE
                qui s'encaisse. */}
            {/* ⭐ ACOMPTE : un règlement partiel sur ce qui reste dû. Le
                plafond est le reste, pas le total — encaisser deux fois la
                même facture ne se rattrape pas d'un clic. */}
            {!estEngagement && vente.status !== 'cancelled' && resteDu(vente) > 0 && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setAcompteOuvert(true)
                }}
              >
                <ListItemIcon>
                  <PaymentsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Saisir un acompte"
                  secondary={`Au plus ${prix(resteDu(vente))}`}
                />
              </MenuItem>
            )}
            {/* ⭐ MODIFIER : réservé à l'administrateur de la boutique
                (ADR-08 amendé). L'immuabilité tenait le commerçant en échec
                sur un cas banal — une erreur de saisie vue dans la minute sur
                une vente déjà validée — et l'avoir, qui répond au
                remboursement, laissait deux documents là où il fallait
                corriger un chiffre. La contrepartie est l'archive : voir
                `ModifierVenteDialog`. */}
            {administrateur && isMutableParAdmin(vente.status) && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setModificationOuverte(true)
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Modifier le document"
                  secondary={
                    estDevis || estCommande
                      ? 'Lignes et montants'
                      : 'Lignes et montants — la version actuelle sera archivée'
                  }
                />
              </MenuItem>
            )}
            {/* ⭐ AVOIR : la seule correction d'une facture émise. On ne
                réécrit pas un document validé, on lui en oppose un autre.
                ⚠️ `docType === 'invoice'` exclut déjà devis ET commande —
                aucun des deux ne s'encaisse, aucun des deux n'a d'avoir. */}
            {vente.docType === 'invoice' &&
              administrateur &&
              vente.status !== 'draft' &&
              vente.status !== 'cancelled' && (
                <MenuItem
                  onClick={() => {
                    setMenu(null)
                    setAvoirOuvert(true)
                  }}
                >
                  <ListItemIcon>
                    <UndoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Générer un avoir"
                    secondary={`Au plus ${prix(vente.totalAmount)}`}
                  />
                </MenuItem>
              )}
            {/* ⚠️ « Annuler » n'a pas de sens sur un devis : il n'y a pas de
                vente à annuler, ni de stock à rendre. Le refus le remplace —
                voir plus haut. Reste la facture ET la commande, qui l'une
                comme l'autre ont pu mouvementer le stock. */}
            {!estDevis && administrateur && vente.status !== 'cancelled' && (
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  setConfirmation(true)
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={estCommande ? 'Annuler la commande' : 'Annuler la vente'}
                  secondary={
                    estCommande ? 'Le stock réservé est rendu' : 'Le stock est rendu'
                  }
                />
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {vente.status !== 'draft' && vente.status !== 'cancelled' && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 4 }}>
          {/* ⚠️ Trois documents, trois corrections. Une vente s'annule et rend
              le stock ; une commande s'annule et rend le stock QU'ELLE avait
              réservé ; un devis n'a jamais rien sorti des rayons — il se
              refuse, et on en refait un. Le texte de la vente promettait un
              mouvement de stock que le devis ne connaît pas. */}
          {estDevis
            ? 'Devis émis — non modifiable. Le client veut changer une ligne ? Marquez celui-ci refusé et refaites-en un : un document numéroté ne se retouche pas.'
            : estCommande
              ? 'Commande validée — non modifiable. Le client veut changer une ligne ? Annulez la commande — le stock réservé est rendu — et repassez-en une nouvelle.'
              : 'Document validé — non modifiable. Une erreur ? Annulez la vente : le stock est rendu et les deux mouvements restent visibles dans l’historique.'}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', md: '1fr 20rem' } }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/*
            ⭐ Un vrai QUADRILLAGE : lignes ET colonnes séparées.

            Sans filets verticaux, six colonnes de chiffres se lisent en
            diagonale — on suit « 12 000 » et on atterrit sur la colonne
            voisine. C'est le détail d'un document comptable : chaque montant
            doit appartenir sans ambiguïté à sa colonne.
          */}
          <Box
            sx={{
              overflowX: 'auto',
              '& th, & td': { borderRight: 1, borderColor: 'divider' },
              '& th:last-of-type, & td:last-of-type': { borderRight: 0 },
              '& thead th': { bgcolor: 'background.default', fontWeight: 700 },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Article</TableCell>
                  <TableCell align="right">Qté</TableCell>
                  <TableCell align="right">P.U. HT</TableCell>
                  <TableCell align="right">TVA</TableCell>
                  <TableCell align="right">Total HT</TableCell>
                  <TableCell align="right">Total TTC</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vente.lines.map((l) => (
                  <TableRow key={l.id} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                    <TableCell>{l.label}</TableCell>
                    <TableCell align="right" className="tabular">
                      {l.quantity}
                    </TableCell>
                    <TableCell align="right" className="tabular">
                      {prix(l.unitPrice)}
                    </TableCell>
                    <TableCell align="right" className="tabular" sx={{ whiteSpace: 'nowrap' }}>
                      {/* Le taux FIGÉ dans la ligne, pas celui de l'article
                          aujourd'hui : une facture émise ne se recalcule pas. */}
                      {l.taxRate > 0 ? `${l.taxRate} %` : '—'}
                    </TableCell>
                    <TableCell align="right" className="tabular">
                      {prix(l.subtotal)}
                    </TableCell>
                    <TableCell align="right" className="tabular" sx={{ fontWeight: 600 }}>
                      {prix(l.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {vente.payments.length > 0 && (
            <Box sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Règlements
              </Typography>
              {vente.payments.map((p) => (
                <Box key={p.id} sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {PAYMENT_METHOD_LABELS[p.method]}
                      {p.createdByName ? ` · ${p.createdByName}` : ''}
                    </Typography>
                    <Typography variant="body2" className="tabular">
                      {prix(p.amount)}
                    </Typography>
                  </Box>
                  {/* ⭐ Ce que le client a tendu et ce qu'on lui a rendu. Rien
                      n'en gardait la trace : seul le montant imputé à la vente
                      était enregistré, et la monnaie rendue disparaissait avec
                      le premier ticket. C'est pourtant la seule chose qu'on
                      cherche quand un client revient contester sa monnaie. */}
                  {p.tendered !== null && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Remis {prix(p.tendered)}
                      {p.change !== null && p.change > 0 ? ` · rendu ${prix(p.change)}` : ''}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 4, borderRadius: 3, height: 'fit-content' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Client
          </Typography>
          <Typography sx={{ mb: 3 }}>{vente.customerName ?? 'Comptoir'}</Typography>

          <Divider sx={{ mb: 3 }} />

          {[
            ['Total HT', vente.subtotalAmount],
            ['Remise', vente.discountAmount],
            ['Taxes', vente.taxAmount],
          ].map(([label, valeur]) => (
            <Box
              key={String(label)}
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {label}
              </Typography>
              <Typography variant="body2" className="tabular">
                {prix(Number(valeur))}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 700 }}>Total TTC</Typography>
            <Typography variant="h3" className="tabular" sx={{ fontWeight: 800 }}>
              {prix(vente.totalAmount)}
            </Typography>
          </Box>
          {/* Ni « Réglé » ni « Reste dû » sur un devis ou une commande : rien
              n'est dû tant qu'ils n'ont pas été transformés en facture, et
              l'afficher en orange transformait un engagement en créance. */}
          {!estEngagement && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Réglé
              </Typography>
              <Typography variant="body2" className="tabular">
                {prix(vente.paidAmount)}
              </Typography>
            </Box>
          )}
          {/* ⭐ Le solde, DANS LES DEUX SENS. La fiche ne montrait que la
              créance : un trop-perçu — monnaie non rendue, règlement en trop —
              n'apparaissait nulle part, et la boutique oubliait ce qu'elle
              devait. Le libellé change avec le sens plutôt que d'afficher un
              nombre négatif, qui se lit mal et se recopie encore plus mal. */}
          {!estEngagement && soldeDocument(vente) !== 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {soldeDocument(vente) > 0 ? 'Reste dû' : 'Avoir client'}
              </Typography>
              <Typography
                variant="body2"
                className="tabular"
                sx={{
                  fontWeight: 700,
                  color: soldeDocument(vente) > 0 ? 'warning.main' : 'error.main',
                }}
              >
                {prix(Math.abs(soldeDocument(vente)))}
              </Typography>
            </Box>
          )}

          {/* ⭐ Le code-barres du document, à l'écran aussi — exactement celui
              du ticket papier. Un client revient avec son ticket : on le
              scanne depuis la fiche pour confirmer qu'il s'agit bien de cette
              vente, sans relire treize chiffres à voix haute. */}
          {ean && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  py: 2,
                  borderRadius: 2,
                }}
              >
                <Barcode value={ean} height={40} moduleWidth={1.6} />
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Un seul document dans le DOM à la fois : les deux portent l'identifiant
          `#ticket` que la règle d'impression isole.

          ⚠️ Rendu en PORTAIL, à la racine du corps de page, et ce n'est pas un
          raffinement. La règle d'impression masquait l'application par
          `visibility: hidden` — or un bloc invisible reste MIS EN PAGE : les
          900 pixels de la fiche continuaient de compter, et le navigateur
          annonçait « 2 feuilles de papier » pour un ticket de dix lignes. La
          seconde sortait vierge, et la thermique la déroulait quand même.
          Sorti de l'arbre de l'application, le document est le seul enfant du
          corps qu'on laisse s'afficher : plus rien d'autre n'occupe de place. */}
      {store && (
        <HistoriqueRevisions storeId={store.storeId} venteId={vente.id} devise={devise} />
      )}

      <PortailImpression>
        {impression === 'ticket' ? (
          <TicketCaisse vente={vente} boutique={store} />
        ) : (
          <FactureImprimable vente={vente} boutique={store} />
        )}
      </PortailImpression>

      <ModifierVenteDialog
        open={modificationOuverte}
        vente={vente}
        devise={devise}
        onClose={() => setModificationOuverte(false)}
        onValider={modifier}
      />

      <MontantDialog
        open={avoirOuvert}
        titre="Générer un avoir"
        description={`Sur la facture ${reference}. Le client se voit créditer ce montant.`}
        avertissement="L’avoir ne remet rien en stock : c’est un document d’argent. Pour un retour de marchandise, annulez la vente — le stock revient alors en rayon."
        plafond={vente.totalAmount}
        devise={devise}
        libelleAction="Émettre l’avoir"
        onClose={() => setAvoirOuvert(false)}
        onValider={emettreAvoir}
      />

      <MontantDialog
        open={acompteOuvert}
        titre="Saisir un acompte"
        description={`Sur ${reference}. Le statut suivra les montants : partiellement réglée, puis réglée.`}
        plafond={resteDu(vente)}
        devise={devise}
        libelleAction="Enregistrer"
        avecMoyen
        onClose={() => setAcompteOuvert(false)}
        onValider={enregistrerAcompte}
      />

      {clientOuvert && store && (
        <RattacherClientDialog
          storeId={store.storeId}
          venteId={vente.id}
          onClose={() => setClientOuvert(false)}
          onRattache={() => undefined}
        />
      )}

      {rdvOuvert && store && (
        <PriseRendezVousDialog
          storeId={store.storeId}
          jour={new Date()}
          horaires={store.openingHours ?? horairesParDefaut()}
          fuseau={store.timezone}
          vente={{
            id: vente.id,
            customerId: vente.customerId,
            customerName: vente.customerName,
          }}
          onClose={() => setRdvOuvert(false)}
        />
      )}

      {/* Le même geste côté serveur — clore le document —, trois histoires
          différentes à raconter. */}
      <ConfirmDialog
        open={confirmation}
        title={
          estDevis
            ? 'Marquer ce devis refusé ?'
            : estCommande
              ? 'Annuler cette commande ?'
              : 'Annuler cette vente ?'
        }
        message={
          estDevis
            ? 'Le devis passera en « refusé » et n’ouvrira plus sur une vente. C’est définitif : un devis clos ne se rouvre pas, il se refait. Son numéro reste attribué — la numérotation ne tolère aucun trou.'
            : estCommande
              ? 'Le document passera en « annulé » et le stock réservé sera rendu. Le numéro légal reste attribué : une numérotation continue ne tolère aucun trou. ⚠️ Impossible si cette commande a déjà été transformée en facture — annulez la facture plutôt.'
              : 'Le document passera en « annulé » et le stock sera rendu. Le numéro légal reste attribué : une numérotation continue ne tolère aucun trou.'
        }
        confirmLabel={estDevis ? 'Marquer refusé' : estCommande ? 'Annuler la commande' : 'Annuler la vente'}
        onClose={() => setConfirmation(false)}
        onConfirm={annuler}
      />

      <Dialog open={envoiOuvert} onClose={() => setEnvoiOuvert(false)} fullWidth maxWidth="xs">
        <DialogTitle>Envoyer le document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Adresse électronique"
            helperText="Laissez vide pour utiliser l’adresse du client."
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button onClick={() => setEnvoiOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={() => void envoyer()}>
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={message !== null}
        autoHideDuration={6000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
